import { memo, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Divider,
} from '@mui/material';
import CollapsibleSection from '../../../shared/components/CollapsibleSection';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import ConfigurationStatusChip from './ConfigurationStatusChip';
import OperatorInputsEditor from './OperatorInputsEditor';
import type { ServiceConfiguration, ServiceOffering, ServiceCapability } from '../../../types/backend';

interface SubscribedOfferingDetailPanelProps {
  subscription: ServiceConfiguration;
  offering: ServiceOffering | undefined;
  busy: boolean;
  onToggleCapability: (offeringId: string, capabilityId: string, enabled: boolean) => Promise<void>;
  onSaveOperatorInputs: (offeringId: string, componentName: string, inputs: Record<string, string>) => Promise<boolean>;
  onRevoke: (offeringId: string) => Promise<boolean>;
}

function offeringCapabilities(offering: ServiceOffering | undefined): ServiceCapability[] {
  if (!offering) return [];
  return offering.manifest?.capabilities ?? offering.capabilities ?? [];
}

const SubscribedOfferingDetailPanel = memo<SubscribedOfferingDetailPanelProps>(({
  subscription,
  offering,
  busy,
  onToggleCapability,
  onSaveOperatorInputs,
  onRevoke,
}) => {
  const [inputsExpanded, setInputsExpanded] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const capabilities = offeringCapabilities(offering);
  const enabled = new Set(subscription.enabled_capabilities ?? []);
  const title = offering?.display_name ?? subscription.offering_id;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6">{title}</Typography>
          <ConfigurationStatusChip status={subscription.status} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {subscription.offering_id} · v{subscription.pinned_offering_version}
        </Typography>
      </Box>

      <Box sx={{ px: 3, py: 2, flex: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Capabilities</Typography>
        {capabilities.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            This offering defines no capabilities.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {capabilities.map((cap) => (
              <FormControlLabel
                key={cap.capability_id}
                control={
                  <Switch
                    size="small"
                    checked={enabled.has(cap.capability_id)}
                    disabled={busy}
                    onChange={(e) => onToggleCapability(subscription.offering_id, cap.capability_id, e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">{cap.display_name}</Typography>
                    {cap.description && (
                      <Typography variant="caption" color="text.secondary">{cap.description}</Typography>
                    )}
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mx: 0, py: 0.5 }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <CollapsibleSection
            title="Operator inputs"
            expanded={inputsExpanded}
            onChange={setInputsExpanded}
          >
            <OperatorInputsEditor
              componentInputs={subscription.operator_inputs ?? {}}
              busy={busy}
              onSave={(component, inputs) => onSaveOperatorInputs(subscription.offering_id, component, inputs)}
            />
          </CollapsibleSection>
        </Box>
      </Box>

      <Divider />
      <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button color="error" variant="outlined" disabled={busy} onClick={() => setConfirmRevoke(true)}>
          Revoke subscription
        </Button>
      </Box>

      <DeleteConfirmDialog
        open={confirmRevoke}
        title="Revoke subscription"
        confirmText="Revoke"
        onCancel={() => setConfirmRevoke(false)}
        onConfirm={async () => {
          setConfirmRevoke(false);
          await onRevoke(subscription.offering_id);
        }}
      >
        <Typography variant="body2">
          Revoke <strong>{title}</strong> for this customer? The reconciler will deprovision its capabilities.
        </Typography>
      </DeleteConfirmDialog>
    </Box>
  );
});

SubscribedOfferingDetailPanel.displayName = 'SubscribedOfferingDetailPanel';
export default SubscribedOfferingDetailPanel;
